import { bool, node, number } from "prop-types";
import { createContext, useContext, useMemo, useRef, useState } from "react";

import styled from "styled-components";
import { opacity } from "theme";

import { useAnimationFrame } from "framer-motion";

import FnrIcon from "UI/FnrIcon";

import setLeftTopFromCenter from "Utilities/elementFunctions/setLeftTopFromCenter";

const Container = styled("div")`
  position: relative;
  height: 0px;
  width: 0px;
  overflow: visible;
  ${opacity}
  transition: ${({ $opacity }) =>
    $opacity === 1 ? "opacity 300ms" : "opacity 3s"};
`;

const SDiv = styled("div")`
  position: absolute;
  height: 0px;
  width: 0px;
`;

const minSpeed = 0;
const baseAccelRate = (2 * Math.PI) / 2;
const baseDeccelRate = (2 * Math.PI) / 300;
const defaultMaxRadius = 100;

const IconContainer = ({ children }) => {
  const { maxRadius, active } = useFnrVirtueSignalContext();

  const maxSpeed = useMemo(
    () => Math.PI * Math.random() * (10 / maxRadius),
    [maxRadius]
  ); // radians per second
  const currentRadians = useRef(Math.random() * 2 * Math.PI);
  const timer = useRef(Date.now());
  const speed = useRef(active ? maxSpeed : minSpeed);
  const [direction] = useState(Math.random() > 0.5);
  const xRadius = useMemo(() => Math.random() * maxRadius, [maxRadius]);
  const yRadius = useMemo(() => Math.random() * maxRadius, [maxRadius]);
  const accelRate = useMemo(
    () => baseAccelRate * (defaultMaxRadius / maxRadius),
    [maxRadius]
  );
  const deccelRate = useMemo(
    () => baseDeccelRate * (defaultMaxRadius / maxRadius),
    [maxRadius]
  );
  const ref = useRef(null);

  useAnimationFrame(() => {
    const changeAmount = ((Date.now() - timer.current) / 1000) * speed.current;
    currentRadians.current += direction ? changeAmount : -changeAmount;
    timer.current = Date.now();
    if (active && speed.current < maxSpeed) speed.current += accelRate;
    else if (!active && speed.current > minSpeed) speed.current -= deccelRate;
    if (!active && speed.current < 0) speed.current = 0;
    const newCenter = [
      xRadius * Math.sin(currentRadians.current),
      yRadius * Math.cos(currentRadians.current),
    ];

    setLeftTopFromCenter(ref.current, newCenter);
  });

  return <SDiv ref={ref}>{children}</SDiv>;
};

IconContainer.propTypes = { children: node };

const FnrVirtueSignalContext = createContext();

/**
 * @typedef FnrVirtueSignalContext
 * @type {object}
 * @property {number} maxRadius - The farthest a virtue icon can be from the source element.
 * @property {boolean} active - Are the icons moving fast, or slowing down?
 */

/**
 *
 * @returns {FnrVirtueSignalContext}
 */
const useFnrVirtueSignalContext = () => useContext(FnrVirtueSignalContext);

/**
 *
 * @param {object} param0
 * @param {number} param0.maxRadius - The farthest a virtue icon can be from the source element.
 * @param {boolean} param0.active - Are the icons moving fast, or slowing down?
 * @returns
 */
export default function FnrVirtueSignal({
  maxRadius = defaultMaxRadius,
  active,
}) {
  return (
    <FnrVirtueSignalContext.Provider
      value={{ maxRadius: maxRadius || defaultMaxRadius, active }}
    >
      <Container $opacity={active ? 1 : 0.05}>
        <IconContainer>
          <FnrIcon icon="courage" />
        </IconContainer>
        <IconContainer>
          <FnrIcon icon="justice" />
        </IconContainer>
        <IconContainer>
          <FnrIcon icon="love" />
        </IconContainer>
        <IconContainer>
          <FnrIcon icon="hope rays" />
        </IconContainer>
        <IconContainer>
          <FnrIcon icon="patience" />
        </IconContainer>
        <IconContainer>
          <FnrIcon icon="temperance" />
        </IconContainer>
        <IconContainer>
          <FnrIcon icon="faith" />
        </IconContainer>
        <IconContainer>
          <FnrIcon icon="veracity" />
        </IconContainer>
        <IconContainer>
          <FnrIcon icon="wisdom" />
        </IconContainer>
        <IconContainer>
          <FnrIcon icon="humility" />
        </IconContainer>
      </Container>
    </FnrVirtueSignalContext.Provider>
  );
}
FnrVirtueSignal.propTypes = { maxRadius: number, active: bool };

// export default FnrVirtueSignal;
